import React from "react";
import {
  Chip,
  IconButton,
  Stack,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
  Box,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ScheduleIcon from "@mui/icons-material/Schedule";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useTranslation } from "../../../../context/Internationalization";
import { DateTime } from "luxon";

const ReasonChip = ({ reason }) => {
  const colorMap = {
    FAKE_PROFILE: "error",
    WRONG_TEAM: "warning",
    FALSE_STATS: "default",
  };
  return (
    <Chip size="small" label={reason} color={colorMap[reason] || "default"} />
  );
};

const StatusChip = ({ status, t }) => {
  const map = {
    pending: { icon: <ScheduleIcon fontSize="small" />, color: "warning" },
    accepted: { icon: <DoneAllIcon fontSize="small" />, color: "success" },
    rejected: { icon: <CloseIcon fontSize="small" />, color: "error" },
  };
  const cfg = map[status] || map.pending;
  return (
    <Chip
      size="small"
      icon={cfg.icon}
      label={t(`${status}`)}
      color={cfg.color}
    />
  );
};

// helpers
const formatWhen = (createdAt) => {
  if (!createdAt) return "";
  // support ISO string or epoch millis
  const dt =
    typeof createdAt === "string" && /\D/.test(createdAt)
      ? DateTime.fromISO(createdAt)
      : DateTime.fromMillis(Number(createdAt));
  return dt.isValid ? dt.toFormat("dd.MM.yyyy HH:mm") : "";
};

const getUserName = (u, fallbackId) => {
  if (!u) return fallbackId || "-";
  const fn = (u.firstName || "").trim();
  const ln = (u.lastName || "").trim();
  const full = [fn, ln].filter(Boolean).join(" ");
  return full || fallbackId || "-";
};

const UserCell = ({ user, fallbackId }) => {
  const name = getUserName(user, fallbackId);
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Box>
        <Typography variant="subtitle1" component="span">
          {name}
        </Typography>
        {user?._id && (
          <Typography variant="caption" component="div" color="text.secondary">
            {user._id}
          </Typography>
        )}
      </Box>
    </Stack>
  );
};

const TeamCell = ({ team, fallbackId }) => {
  const name = team.name || fallbackId;
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Box>
        <Typography variant="subtitle1" component="span">
          {name}
        </Typography>
        {team?._id && (
          <Typography variant="caption" component="div" color="text.secondary">
            {team._id}
          </Typography>
        )}
      </Box>
    </Stack>
  );
};

const BanReportsTableRow = ({ report, lastElementRef, onOpenReview }) => {
  const { t } = useTranslation();

  return (
    <TableRow
      ref={lastElementRef}
      key={report._id}
      sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
    >
      {/* Reported user (target) */}
      <TableCell>
        <UserCell
          user={report.reportedUser}
          fallbackId={report.reportedUserId}
        />
      </TableCell>

      {/* Reported team (target) */}
      <TableCell>
        <TeamCell
          team={report.reportedTeam}
          fallbackId={report.reportedTeamId}
        />
      </TableCell>

      {/* Reporter user */}
      <TableCell>
        <UserCell
          user={report.reporterUser}
          fallbackId={report.reporterUserId}
        />
      </TableCell>

      <TableCell>
        <ReasonChip reason={report.reason} />
      </TableCell>

      <TableCell>
        <Tooltip title={report.comment || ""} arrow>
          <Typography
            variant="body2"
            component="span"
            noWrap
            sx={{ maxWidth: 260, display: "inline-block" }}
          >
            {report.comment || "-"}
          </Typography>
        </Tooltip>
      </TableCell>

      <TableCell>
        <StatusChip status={report.status} t={t} />
      </TableCell>

      <TableCell>
        <Typography variant="body2" component="span">
          {formatWhen(report.createdAt)}
        </Typography>
      </TableCell>

      <TableCell>
        <Stack direction="row" spacing={1}>
          {report.status === "pending" ? (
            <Tooltip title={t("review")}>
              <IconButton size="small" onClick={() => onOpenReview(report)}>
                <FactCheckIcon color="warning" />
              </IconButton>
            </Tooltip>
          ) : report.status === "accepted" ? (
            <>
              <Tooltip title={t("view_decision")}>
                <IconButton size="small" onClick={() => onOpenReview(report)}>
                  <VisibilityIcon />
                </IconButton>
              </Tooltip>
            </>
          ) : report.status === "rejected" ? (
            <>
              <Tooltip title={t("view_decision")}>
                <IconButton size="small" onClick={() => onOpenReview(report)}>
                  <VisibilityIcon />
                </IconButton>
              </Tooltip>
            </>
          ) : null}
        </Stack>
      </TableCell>
    </TableRow>
  );
};

export default BanReportsTableRow;
